import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import {
  getAnalyticsClients,
  type AnalyticsPeriodParams,
  type ClientAnalyticsRow,
  type ConversionRow,
} from '../../shared/api/analytics.api'
import { Spinner } from '../../shared/ui/Spinner'
import { ErrorMessage } from '../../shared/ui/ErrorMessage'
import { useAnalyticsTable, type ColumnDef } from './shared/useAnalyticsTable'
import { SortableTh } from './shared/SortableTh'

/**
 * Вкладка «Клиенты» модуля «Аналитика» (этап 4 docs/analytics-plan.md).
 * Конверсия по сегменту/категории, таблица клиентов (топ/антитоп), «спящие».
 * Таблица клиентов — сортировка + фильтры (клиент/сегмент/категория).
 */

/** Цвет конверсии: высокая — зелёная, низкая — красная. */
const convClass = (pct: number): string =>
  pct >= 30 ? 'text-emerald-600' : pct >= 10 ? 'text-amber-600' : 'text-red-600'

const CLIENT_COLUMNS: ColumnDef<ClientAnalyticsRow>[] = [
  { key: 'name', sort: (r) => r.name.toLowerCase(), filter: (r) => r.name },
  { key: 'segment', sort: (r) => r.segment, filter: (r) => r.segment },
  { key: 'category', sort: (r) => r.category, filter: (r) => r.category },
  { key: 'requests', sort: (r) => r.requests },
  { key: 'won', sort: (r) => r.won },
  { key: 'conversion', sort: (r) => r.conversion },
]

// Плавная заливка бара: hue идёт от 0° (красный) к 120° (зелёный) пропорционально
// проценту конверсии — 50% попадает в жёлтый. saturation/lightness фиксированы,
// бар сохраняет читаемость на белом фоне.
function conversionBarColor(pct: number): string {
  const clamped = Math.max(0, Math.min(100, pct))
  const hue = clamped * 1.2
  return `hsl(${hue}, 72%, 48%)`
}

function ConversionBlock({ title, rows }: { title: string; rows: ConversionRow[] }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
      <div className="mt-3 space-y-1.5">
        {rows.map((r) => {
          const pct = Math.max(0, Math.min(100, r.conversion))
          return (
            <div key={r.key} className="flex items-center gap-3">
              <div className="w-44 text-sm text-gray-700 flex-shrink-0 truncate" title={r.key}>
                {r.key}
              </div>
              <div className="flex-1 bg-gray-100 rounded-md h-6 overflow-hidden">
                <div
                  className="h-full rounded-md transition-all"
                  style={{
                    width: `${pct}%`,
                    backgroundColor: conversionBarColor(pct),
                  }}
                />
              </div>
              <div className="w-28 text-right text-xs text-gray-500 flex-shrink-0">
                {r.won}/{r.total} ·{' '}
                <span className={`font-medium ${convClass(r.conversion)}`}>{r.conversion}%</span>
              </div>
            </div>
          )
        })}
        {rows.length === 0 && <p className="text-sm text-gray-400">Нет данных за период</p>}
      </div>
    </div>
  )
}

export function ClientsTab({ periodParams }: { periodParams: AnalyticsPeriodParams }) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['analytics-clients', periodParams],
    queryFn: () => getAnalyticsClients(periodParams),
  })

  const table = useAnalyticsTable(data?.clients ?? [], CLIENT_COLUMNS, undefined, 'clients')

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner />
      </div>
    )
  }
  if (isError || !data) {
    return <ErrorMessage error="Не удалось загрузить аналитику по клиентам" />
  }

  return (
    <div className="space-y-4">
      <div className="grid lg:grid-cols-2 gap-4">
        <ConversionBlock title="Конверсия в заказ по сегменту" rows={data.bySegment} />
        <ConversionBlock title="Конверсия в заказ по категории" rows={data.byCategory} />
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <div className="flex items-baseline justify-between flex-wrap gap-2">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">
              Клиенты — объём заявок vs конверсия
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">
              Все клиенты периода по числу заявок. Красная конверсия при большом объёме — «много
              заявок, мало заказов». Клик по заголовку — сортировка, воронка — фильтр.
            </p>
          </div>
          <span className="text-xs text-gray-500">
            «Спящих» клиентов за период:{' '}
            <span className="font-semibold text-gray-900">{data.sleepingCount}</span>
          </span>
        </div>
        <div className="mt-3 max-h-[32rem] overflow-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-gray-500 border-b border-gray-200 [&_th]:sticky [&_th]:top-0 [&_th]:bg-white [&_th]:z-10">
                <SortableTh table={table} col="name" className="pr-3">
                  Клиент
                </SortableTh>
                <SortableTh table={table} col="segment" className="px-3">
                  Сегмент
                </SortableTh>
                <SortableTh table={table} col="category" className="px-3">
                  Кат.
                </SortableTh>
                <SortableTh table={table} col="requests" align="right" className="px-3">
                  Заявок
                </SortableTh>
                <SortableTh table={table} col="won" align="right" className="px-3">
                  В заказ
                </SortableTh>
                <SortableTh table={table} col="conversion" align="right" className="pl-3">
                  Конверсия
                </SortableTh>
              </tr>
            </thead>
            <tbody>
              {table.view.map((c) => (
                <tr key={c.clientId} className="border-b border-gray-50">
                  <td className="py-2 pr-3">
                    <Link
                      to={`/clients/${c.clientId}`}
                      className="text-brand-600 hover:text-brand-700 font-medium"
                    >
                      {c.name}
                    </Link>
                  </td>
                  <td className="py-2 px-3 text-gray-600">{c.segment}</td>
                  <td className="py-2 px-3 text-gray-600">{c.category}</td>
                  <td className="py-2 px-3 text-right text-gray-800">{c.requests}</td>
                  <td className="py-2 px-3 text-right text-gray-600">{c.won}</td>
                  <td className={`py-2 pl-3 text-right font-medium ${convClass(c.conversion)}`}>
                    {c.conversion}%
                  </td>
                </tr>
              ))}
              {table.view.length === 0 && (
                <tr>
                  <td colSpan={6} className="py-6 text-center text-sm text-gray-400">
                    Нет заявок за период
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
